/* eslint-disable indent */
// @flow
import React, { useState, useEffect, useCallback } from 'react';
import { withRouter } from 'react-router';
import api from 'api';
import { useSelector } from 'react-redux';
import { appContentbySlugSelector } from '../selectors';
import { errorLogger, infoLogger } from '../../helpers';
import { CustomerTypes, ToggleKeys, TransferSteps } from '../../Constants';
import NotEntitled from '../NotEntitled';
import Loading from 'components';
import Maintenance from '../Maintenance';
import Error from '../Error';
import Login from '../Login';
import SmsChallenge from 'views/SmsChallenge';
import Confirm from 'views/Confirm';
import DoTransfer from '../DoTransfer';
import Complete from '../Complete';
import { isComviqCustomer } from '../../helpers/customerTypeHelpers';
import { isToggledOnn } from '../../helpers/togglesHelper';
import addContactPhoneNumberToText, { getContentFor } from '../../helpers/contentHelpers';

type Props = {
  match: {
    params: {
      iccid: String,
      imsi: string,
      eid: string,
    },
  },
};

const Transfer = ({
  match: {
    params: { iccid: testIccId, imsi: testImsi, eid: testEid },
  },
}: Props) => {
  const [isLoading, setIsLoading] = useState(true);
  const [showWall, setShowWall] = useState(false);
  const [isError, setIsError] = useState(false);
  const [transferOrderId, setTransferOrderId] = useState('');
  const [userData, setUserData] = useState({});
  const [entitledStatus, setEntitledStatus] = useState({
    errorMessage: '',
    isInvalidCustomerType: false,
    isAllowedTransfer: true,
  });
  const [currentStep, setCurrentStep] = useState(TransferSteps.LOGIN);
  const customerType = useSelector((state) => state.app.customerType);
  const msisdn = useSelector((state) => state.app.msisdn);
  const toggles = useSelector((state) => state.app.togglesNew);
  // const customerTypeContentKey = useSelector((state) => state.app.customerTypeContentKey);
  // const filterslug = (typeof customerTypeContentKey !== 'undefined' ? customerTypeContentKey : '') + 'home';
  // const content = useSelector((state) => appContentbySlugSelector(state, filterslug));
  const isComviq = isComviqCustomer(customerType);
  const content = getContentFor(customerType);
  function setWallToggle(key) {
    const isToggledOn = isToggledOnn({ toggles, key });

    setShowWall(isToggledOn);
  }

  const handleMaintenanceSuccessfullLogin = useCallback(() => {
    setShowWall(false);
  }, []);

  const handleLoggedInStatus = (isLoggedIn) => {
    if (isLoggedIn) {
      setCurrentStep(isComviq ? TransferSteps.DO_TRANSFER : TransferSteps.CONFIRM);
    }
  };

  const setUserAuthData = (data) => {
    setUserData(data);
    if (data.errorMessage) {
      updateUserEntitled(data.errorMessage);
      setIsError(true);
    }
  };

  const updateUserEntitled = (errorMessage = '') => {
    const isInvalidCustomerType = customerType === CustomerTypes.INVALID_CUSTOMER_TYPE;
    const isAllowedTransfer = customerType !== CustomerTypes.MELODY;
    setEntitledStatus({
      errorMessage,
      isInvalidCustomerType,
      isAllowedTransfer,
    });
  };

  const handleDoTransfer = async () => {
    try {
      const { msisdn, currentIccid, secondaryEid } = userData;

      const infoLog = `Handeling doTransfer with msisdn: ${msisdn} & currentIccid: ${currentIccid} & secondaryEid: ${secondaryEid}`;
      infoLogger({ name: 'client-do-transfer', log: infoLog });

      if (msisdn) {
        const reservedIccid = await reserveSimProfile({
          PrimaryMsisdn: msisdn,
          Eid: secondaryEid,
        });

        if (reservedIccid) {
          const transfer = await postTransfer({
            PrimaryMsisdn: msisdn,
            CurrentIccid: currentIccid,
            NewIccid: reservedIccid,
            SecondaryEid: secondaryEid,
          });

          if (transfer.isSuccess) {
            setCurrentStep(TransferSteps.COMPLETE);
          }
        }
      }
    } catch (ex) {
      errorLogger({ name: 'client-post-order', log: `Transfer misslyckades: ${ex.message}` });
      updateUserEntitled(`Transfer misslyckades: ${ex.message}`);
      setIsError(true);
    }
  };

  const reserveSimProfile = async (body) => {
    let reservedIccid = null;
    try {
      const result = await api.reserveSimProfile(body);
      if (result && result.status === 201) {
        reservedIccid = result.data.iccid;
      } else {
        errorLogger({ name: 'client-post-order', log: `Profilen kunde inte reserveras` });
        updateUserEntitled('Profilen kunde inte reserveras');
        setIsError(true);
      }
    } catch (ex) {
      errorLogger({ name: 'client-post-order', log: `Profilen kunde inte reserveras: ${ex.message}` });
      updateUserEntitled('Profilen kunde inte reserveras');
      setIsError(true);
    }

    return reservedIccid;
  };

  const postTransfer = async (body) => {
    let isSuccess = false;

    try {
      const result = await api.postTransferOrder(body);

      if (result && result.orderId) {
        isSuccess = true;
        setTransferOrderId(result.orderId);
        infoLogger({ name: 'client-transfer', log: `Transfer returned without error.` });
        const customerBody = {
          Eid: body.secondaryEid,
          Msisdn: body.PrimaryMsisdn,
          HasUncompletedOrder: true,
          OrderId: result.orderId,
          CustomerType: customerType,
          Vendor: window.__VENDOR__,
        };
        await api.rest('v1/customerStates', customerBody, 'POST');
      } else {
        const errorMessage = `Error in post transfer: ${
          result && result.error ? result.error : 'Transfer misslyckades'
        }`;
        updateUserEntitled(errorMessage);
        setIsError(true);
      }
    } catch ({ message }) {
      errorLogger({ name: 'client-post-order', log: `Transfer failed: ${message}` });
      updateUserEntitled(message);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }

    return { isSuccess };
  };

  const handleCloseWebSheet = () => {
    infoLogger({ name: 'client-cancel-transfer', log: 'User canseled transfer' });
    // eslint-disable-next-line no-undef
    DataActivationController.doneSelected();
  };

  const handleConfirmWatch = useCallback(() => {
    setCurrentStep(TransferSteps.DO_TRANSFER);
  });

  useEffect(() => {
    if (customerType) {
      setWallToggle(ToggleKeys.VENDOR[window.__VENDOR__] + customerType + '-wall');
      updateUserEntitled();
      setIsLoading(false);
    }
  }, [customerType]);

  const renderStepTele2 = () => {
    switch (currentStep) {
      case TransferSteps.LOGIN:
        return renderLogin();
      case TransferSteps.CONFIRM:
        return <Confirm onConfirm={handleConfirmWatch} device={userData.name} isScrolled={false} />;
      case TransferSteps.DO_TRANSFER:
        return (
          <DoTransfer
            onDoTransfer={handleDoTransfer}
            onCancel={handleCloseWebSheet}
            hasUncompletedTransfer={userData.hasUncompletedTransfer}
          />
        );
      case TransferSteps.COMPLETE:
        return <Complete orderId={transferOrderId} msisdn={msisdn} eId={userData.secondaryEid} isTransfer={true} />;
    }
  };

  const renderLogin = () => {
    const useSsnLogin = customerType === CustomerTypes.FUTUR || isComviq;
    const useSmsLogin = customerType === CustomerTypes.FUTUR_MICRO;
    return useSsnLogin ? (
      <Login
        setLoggedInStatus={handleLoggedInStatus}
        setUserAuthData={setUserAuthData}
        msisdn={testImsi}
        iccid={testIccId}
        eid={testEid}
        showPromotion={false}
        isTransfer={true}
      />
    ) : useSmsLogin ? (
      <SmsChallenge
        setLoggedInStatus={handleLoggedInStatus}
        setUserAuthData={setUserAuthData}
        eid={testEid}
        iccid={testIccId}
        isTransfer={true}
      />
    ) : (
      <Error message={content.page.entitled.numberNotEligibleError.htmlText} />
    );
  };

  if (isLoading || !customerType) return <Loading />;

  if (showWall)
    return <Maintenance onValidLogin={handleMaintenanceSuccessfullLogin} message={content.page.maintenance.htmlText} />;

  if (isError) return <Error message={entitledStatus.errorMessage} />;

  if (!entitledStatus.isAllowedTransfer || entitledStatus.isInvalidCustomerType)
    return (
      <NotEntitled
        isAllowedTransfer={entitledStatus.isAllowedTransfer}
        errorMessage={entitledStatus.errorMessage}
        isInvalidCustomerType={entitledStatus.isInvalidCustomerType}
      />
    );

  return <>{renderStepTele2()}</>;
};

export default withRouter(Transfer);
